/* eslint-disable import/no-extraneous-dependencies, no-plusplus */
import { Grid } from '@mui/material';
import { BasicButton } from '@tassat/button';
import { SecondaryApiTypeEnum } from '@tassat/common-definitions';
import {
    CheckboxField,
    SelectField,
    SimpleErrorMessage,
    TextField
} from '@tassat/formik-input';
import { ActionTypes, SelectOption } from '@tassat/ui-definitions';
import { debounceExecution } from '@tassat/utility';
import { Formik, FormikProps } from 'formik';
import * as React from 'react';

import { validateClientUser } from './submission';
import {
    IFormProps,
    IFormValues,
    IFormikProps,
    isUserRecord
} from './types';

const FormContents = (props: IFormProps) => {
    const {
        classes,
        firm,
        values,
        errors,
        submitForm,
        isValid,
        isSubmitting,
        dirty,
        formProps,
        submitError,
        setValues,
        onClose,
        isUsernameValidated,
        setIsUsernameValidated,
        setFieldValue,
        validateForm
    } = props;

    const {
        actionType,
        deleteUser,
        editUser,
        features,
        firmListOptions,
        firmSecondaryApiConnections
    } = formProps;

    const showSecondaryApiInput = features?.isSecondaryApiEnabled &&
        firmSecondaryApiConnections?.some((api) => api.APISecondaryConnectionType === SecondaryApiTypeEnum.FIREBLOCKS);

    React.useEffect(() => {
        if (values.Firm?.refData) {
            setValues({
                ...values,
                CompanyCode: values.Firm.refData?.CompanyCode
            });
        }
    }, [values.Firm]);

    React.useEffect(() => {
        if (features?.loginType === 'Auth0' && !features?.SSO) {
            setValues({
                ...values,
                Username: values.Email
            });
        }
    }, [ features?.loginType, features?.SSO, values.Email ]);

    React.useEffect(() => {
        if (!(actionType === ActionTypes.edit || (actionType === ActionTypes.add && features?.loginType === 'Auth0' && !features.SSO))) {
            setIsUsernameValidated(false);
        }
    }, [values.Username]);

    React.useEffect(() => {
        if (!isUsernameValidated) {
            validateForm();
        }
    }, [isUsernameValidated]);

    return (
        <form className={classes.flexColumn}>
            <Grid
                container
                direction='column'
                spacing={0}
            >
                <Grid
                    className={classes.fullWidth}
                    container
                    item
                    spacing={2}
                    xs={12}
                >
                    <Grid
                        item
                        xs={6}
                    >
                        <TextField
                            disabled={
                                firm?.IsClosed ||
                                (features?.SSO && actionType === ActionTypes.edit) ||
                                (features?.SSOConfig?.CompanyCodeRequired && actionType === ActionTypes.sso_validate)
                            }
                            errors={errors}
                            label='First Name'
                            maxLength={48}
                            name='FirstName'
                            placeholder='First'
                            values={values}
                        />
                    </Grid>
                    <Grid
                        item
                        sm={6}
                    >
                        <TextField
                            disabled={
                                firm?.IsClosed ||
                                (features?.SSO && actionType === ActionTypes.edit) ||
                                (features?.SSOConfig?.CompanyCodeRequired && actionType === ActionTypes.sso_validate)
                            }
                            errors={errors}
                            label='Last Name'
                            maxLength={48}
                            name='LastName'
                            placeholder='Last'
                            values={values}
                        />
                    </Grid>
                </Grid>
                <Grid
                    className={classes.fullWidth}
                    container
                    item
                    spacing={2}
                    xs={12}
                >
                    <Grid
                        item
                        sm={6}
                        test-id='client-user-form--username'
                    >
                        <TextField
                            disabled={
                                actionType === ActionTypes.edit ||
                                firm?.IsClosed ||
                                (actionType === ActionTypes.sso_validate && features?.SSO && features?.SSOConfig?.CompanyCodeRequired) ||
                                (actionType === ActionTypes.add && features?.loginType === 'Auth0' && !features?.SSO)
                            }
                            errors={errors}
                            label={features?.SSO ? 'SSO User ID' : 'Username'}
                            maxLength={features?.SSO ? 256 : 64}
                            name='Username'
                            placeholder={features?.SSO ? 'SSO User ID' : 'Username'}
                            values={values}
                        />
                    </Grid>
                    <Grid
                        item
                        sm={6}
                    >
                        <TextField
                            disabled={
                                firm?.IsClosed ||
                                (actionType === ActionTypes.edit) ||
                                (actionType === ActionTypes.sso_validate && features?.SSO && features?.SSOConfig?.CompanyCodeRequired)
                            }
                            errors={errors}
                            label='Email'
                            maxLength={64}
                            name='Email'
                            placeholder='Email'
                            values={values}
                        />
                    </Grid>
                    <Grid
                        className={classes.fullWidth}
                        container
                        item
                        spacing={2}
                        xs={12}
                    >
                        <Grid
                            item
                            sm={6}
                        >
                            <TextField
                                disabled={
                                    firm?.IsClosed ||
                                    (features?.SSO && actionType === ActionTypes.edit) ||
                                    (features?.SSOConfig?.CompanyCodeRequired && actionType === ActionTypes.sso_validate)
                                }
                                errors={errors}
                                label='Phone'
                                maxLength={32}
                                name='Phone'
                                placeholder='Phone Number'
                                values={values}
                            />
                        </Grid>
                        {firm?.IsTokenAPIFirm && (
                            <Grid
                                className={classes.checkboxField}
                                item
                            >
                                <CheckboxField
                                    disabled={firm?.IsClosed}
                                    errors={errors}
                                    label='Token API'
                                    name='IsTokenAPIUser'
                                    values={values}
                                />
                            </Grid>
                        )}
                        {showSecondaryApiInput && (
                            // TODO Update in the future to handle multiple API connections
                            <Grid
                                className={classes.checkboxField}
                                item
                            >
                                <CheckboxField
                                    disabled={firm?.IsClosed}
                                    errors={errors}
                                    label='Fireblocks API'
                                    name='APISecondaryConnectionType'
                                    values={values}
                                />
                            </Grid>
                        )}
                    </Grid>
                    {features?.SSO && (
                        <>
                            {!(ActionTypes.sso_add === actionType || ActionTypes.edit === actionType) && firmListOptions && (
                                <Grid
                                    item
                                    sm={6}
                                    test-id='firms-selectors'
                                >
                                    <SelectField
                                        disabled={Boolean(features?.SSO && values?.CompanyCode && actionType === ActionTypes.sso_validate)}
                                        errors={errors}
                                        label='Firm'
                                        name='Firm'
                                        options={firmListOptions}
                                        placeholder='Select'
                                        values={values}
                                    />
                                </Grid>
                            )}
                            {features?.SSOConfig?.CompanyCodeRequired &&
                                !(ActionTypes.sso_add === actionType || ActionTypes.edit === actionType) && (
                                <Grid
                                    item
                                    sm={6}
                                    test-id='company-code-field'
                                >
                                    <TextField
                                        disabled={
                                            firm?.IsClosed ||
                                            (features?.SSOConfig?.CompanyCodeRequired && actionType === ActionTypes.sso_validate)
                                        }
                                        errors={errors}
                                        label='Company Code'
                                        maxLength={256}
                                        name='CompanyCode'
                                        placeholder='Company Code'
                                        values={values}
                                    />
                                </Grid>
                            )}
                        </>
                    )}
                    <Grid
                        item
                        sm={6}
                    >
                        <TextField
                            disabled={firm?.IsClosed}
                            errors={errors}
                            label='Notes'
                            maxLength={50}
                            name='Notes'
                            placeholder='Notes'
                            values={values}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid
                alignItems='center'
                className={classes.submitButtonContainer}
                container
                item
                justifyContent='space-evenly'
                xs={12}
            >
                {actionType === ActionTypes.edit && deleteUser && (
                    <Grid item>
                        <BasicButton
                            onClick={() => deleteUser(editUser)}
                            text='Delete Client User'
                            type='action'
                        />
                    </Grid>
                )}
                {actionType === ActionTypes.sso_validate && onClose && (
                    <BasicButton
                        className={classes.cancelButton}
                        id='auth_code_cancel_button'
                        onClick={onClose}
                        text='Cancel'
                        type='noBgBlue'
                    />
                )}
                <Grid
                    className={classes.submitButton}
                    container
                    direction='column'
                    item
                >
                    <BasicButton
                        disabled={
                            actionType !== ActionTypes.sso_validate && !dirty ||
                            isSubmitting ||
                            !isValid ||
                            firm?.IsClosed || !isUsernameValidated
                        }
                        onClick={submitForm}
                        text='Submit'
                        type='submit'
                    />
                    <SimpleErrorMessage
                        className={classes.submitError}
                        error={submitError}
                    />
                </Grid>
            </Grid>
        </form>
    );
};

export default function ClientUserForm(props: IFormikProps) {
    const { classes, onClose, formProps } = props;

    const {
        actionType,
        editUser,
        firmId,
        users,
        firm,
        isUserMappedToApiConnection,
        submitClientUser,
        features,
        firmListOptions,
        validateUsernameAsync,
        firmSecondaryApiConnections,
        userSecondaryApiConnections,
        isUserMappedToSecondaryApi
    } = formProps;

    const [ submitError, setSubmitError ] = React.useState<string>('');
    const [ isUsernameValidated, setIsUsernameValidated ] = React.useState<boolean>((actionType === ActionTypes.edit || (actionType === ActionTypes.add && features?.loginType === 'Auth0' && !features.SSO)) ? true : false);
    const debouncedUsernameValidation = React.useRef(debounceExecution<string | null>(validateUsernameAsync, 1000));

    let initialValues = {} as IFormValues;
    if (actionType === ActionTypes.add || actionType === ActionTypes.sso_add) {
        initialValues = {
            FirstName: '',
            LastName: '',
            IsTokenAPIUser: false,
            Email: '',
            Phone: '',
            Notes: '',
            Username: '',
            CompanyCode: undefined,
            Firm: {} as SelectOption,
            APISecondaryConnectionType: undefined
        };
    } else if (actionType === ActionTypes.edit || actionType === ActionTypes.sso_validate) {
        if (!editUser) return <div />;
        if (isUserRecord(editUser)) {
            const isApiSecondaryEnabled =
                features?.isSecondaryApiEnabled &&
                (firmSecondaryApiConnections?.some((api) => api.APISecondaryConnectionType === SecondaryApiTypeEnum.FIREBLOCKS)) &&
                (userSecondaryApiConnections?.some((api) => api.APISecondaryConnectionType === SecondaryApiTypeEnum.FIREBLOCKS));
            initialValues = {
                ...editUser,
                APISecondaryConnectionType: isApiSecondaryEnabled
            };
        } else {
            initialValues = {
                ...editUser,
                Firm: firmListOptions?.find((firm) => firm.refData.CompanyCode === editUser.CompanyCode),
                Notes: ''
            };
        }
    }

    const clientUserOriginalValidations = (values: IFormValues) => validateClientUser(values, initialValues, {
        actionType,
        users,
        firmId,
        isUserMappedToApiConnection,
        features,
        debouncedUsernameValidation: debouncedUsernameValidation.current,
        isUsernameValidated,
        setIsUsernameValidated,
        isUserMappedToSecondaryApi
    });

    return (
        <Grid
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid
                item
                sm={12}
            >
                <Formik
                    initialValues={initialValues}
                    onSubmit={async (values: IFormValues) => {
                        const {
                            Firm,
                            ...rest
                        } = values;
                        await submitClientUser(rest, {
                            actionType,
                            userId: (editUser && isUserRecord(editUser)) ? editUser.UserId : undefined,
                            firmId: firmId ? firmId : values.Firm?.value as string,
                            setSubmitError,
                            onClose,
                            firmName: values?.Firm?.label,
                            secondaryApiFeatureEnabled: features?.isSecondaryApiEnabled,
                            initialValues
                        });
                    }}
                    validate={clientUserOriginalValidations}
                    validateOnChange
                    validateOnMount
                >
                    {(formContentsProps: FormikProps<IFormValues>) => (
                        <FormContents
                            {...formContentsProps}
                            classes={classes}
                            firm={firm || initialValues.Firm?.refData}
                            formProps={formProps}
                            isUsernameValidated={isUsernameValidated}
                            onClose={onClose}
                            setIsUsernameValidated={setIsUsernameValidated}
                            submitError={submitError}
                        />
                    )}
                </Formik>
            </Grid>
        </Grid>
    );
}